import { supabase } from '../lib/supabase';

export interface AddressMarker {
  id?: string;
  business_id: string;
  customer_id?: string;
  latitude: number;
  longitude: number;
  marker_type: 'not-home' | 'booked' | 'comeback-later' | 'interested' | 'estimate-sent' | 'bad-customer' | 'customer' | 'prospect' | 'not-interested';
  address?: string;
  notes?: string;
  /** CRM opportunity created from this sales pin (door-to-door lead capture). */
  opportunity_id?: number | null;
  /** Google Business listing details when the pin was created from a business search. */
  google_place_name?: string | null;
  google_place_phone?: string | null;
  google_place_website?: string | null;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  customer?: {
    id: string;
    first_name: string;
    last_name: string;
    business_name?: string;
    email?: string;
    phone?: string;
  };
}

export interface CustomerSyncResult {
  created: number;
  updated: number;
  removed: number;
  errors: string[];
}

class AddressMarkerService {
  private getEdgeFunctionUrl(): string {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!supabaseUrl) {
      throw new Error('Supabase URL not available');
    }
    return `${supabaseUrl}/functions/v1/address-markers`;
  }

  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.getEdgeFunctionUrl()}${endpoint}`;
    
    const { data: { session } } = await supabase!.auth.getSession();
    const token = session?.access_token;

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Network error' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'Request failed');
    }

    return result.data || result;
  }

  // Sync customers with addresses to create map markers
  async syncCustomers(businessId: string): Promise<CustomerSyncResult> {
    console.log(`Starting customer sync for business: ${businessId}`);
    
    try {
      const result = await this.makeRequest<{ results: CustomerSyncResult }>(`/sync-customers/${businessId}`, {
        method: 'POST',
      });
      
      console.log('Customer sync completed:', result.results);
      return result.results;
    } catch (error) {
      console.error('Error syncing customers:', error);
      throw error;
    }
  }

  // Get all address markers for a business
  async getMarkers(businessId: string): Promise<AddressMarker[]> {
    return this.makeRequest<AddressMarker[]>(`/${businessId}`);
  }

  // Create a new address marker
  async createMarker(marker: Omit<AddressMarker, 'id' | 'created_at' | 'updated_at'>): Promise<AddressMarker> {
    return this.makeRequest<AddressMarker>('', {
      method: 'POST',
      body: JSON.stringify(marker),
    });
  }

  // Update an existing address marker
  async updateMarker(
    markerId: string, 
    updates: Partial<Pick<AddressMarker, 'marker_type' | 'address' | 'notes'>>
  ): Promise<AddressMarker> {
    return this.makeRequest<AddressMarker>(`/${markerId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  // Delete an address marker
  async deleteMarker(markerId: string): Promise<void> {
    await this.makeRequest(`/${markerId}`, {
      method: 'DELETE',
    });
  }

  // Helper function to get marker color based on type
  getMarkerColor(markerType: AddressMarker['marker_type']): string {
    const colorMap: Record<AddressMarker['marker_type'], string> = {
      'not-home': '#9CA3AF',        // Gray
      'prospect': '#F97316',        // Orange
      'booked': '#10B981',          // Green
      'comeback-later': '#F59E0B',  // Yellow/Orange
      'interested': '#3B82F6',      // Blue
      'estimate-sent': '#8B5CF6',   // Purple
      'bad-customer': '#EF4444',    // Red
      'customer': '#06B6D4',        // Cyan
      'not-interested': '#DC2626',  // Dark red (hard no)
    };
    return colorMap[markerType] || '#9CA3AF';
  }

  // Helper function to get marker icon based on type
  getMarkerIcon(markerType: AddressMarker['marker_type']): string {
    const iconMap: Record<AddressMarker['marker_type'], string> = {
      'not-home': '🏠',
      'prospect': '🎯',
      'booked': '✅',
      'comeback-later': '⏰',
      'interested': '👀',
      'estimate-sent': '📄',
      'bad-customer': '❌',
      'customer': '👤',
      'not-interested': '🚫',
    };
    return iconMap[markerType] || '📍';
  }

  /**
   * Customer initials to render inside a sales pin badge when the pin is
   * linked to a customer/lead (e.g. "JD"). Empty string when unlinked.
   */
  getSalesPinInitials(marker: Pick<AddressMarker, 'customer'>): string {
    const c = marker.customer;
    if (!c) return '';
    const letters = `${(c.first_name || '').trim().charAt(0)}${(c.last_name || '').trim().charAt(0)}`
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');
    return letters;
  }

  /**
   * Sales pin map icon: a rounded-square badge colored by knock status with a
   * white border — deliberately distinct from teardrop booking pins and the
   * circular resource-location dots. When the pin is linked to a lead, the
   * connected customer's initials render inside the badge. Call only after the
   * Google Maps script has loaded.
   */
  getSalesPinIcon(
    markerType: AddressMarker['marker_type'],
    initials?: string,
  ): google.maps.Icon {
    const color = this.getMarkerColor(markerType);
    const label = (initials || '').slice(0, 2);
    const svg =
      `<svg xmlns='http://www.w3.org/2000/svg' width='30' height='30' viewBox='0 0 30 30'>` +
      `<rect x='3' y='3' width='24' height='24' rx='8' fill='${color}' stroke='#ffffff' stroke-width='2.5'/>` +
      (label
        ? `<text x='15' y='15.5' text-anchor='middle' dominant-baseline='central' ` +
          `font-family='-apple-system,Helvetica,Arial,sans-serif' font-size='11' font-weight='700' ` +
          `fill='#ffffff'>${label}</text>`
        : `<circle cx='15' cy='15' r='3.5' fill='#ffffff' fill-opacity='0.9'/>`) +
      `</svg>`;
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new google.maps.Size(30, 30),
      anchor: new google.maps.Point(15, 15),
    };
  }

  // Helper function to get human-readable marker type
  getMarkerTypeLabel(markerType: AddressMarker['marker_type']): string {
    const labelMap: Record<AddressMarker['marker_type'], string> = {
      'not-home': 'Not Home',
      'prospect': 'Prospect',
      'booked': 'Closed',
      'comeback-later': 'Come Back Later',
      'interested': 'Interested',
      'estimate-sent': 'Estimate Sent',
      'bad-customer': 'Bad Customer',
      'customer': 'Customer',
      'not-interested': 'Not Interested',
    };
    return labelMap[markerType] || markerType;
  }
}

export const addressMarkerService = new AddressMarkerService(); 